import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";

type Gorunum = "birincil" | "ikincil" | "sessiz" | "tehlike" | "kesikli";
type Boyut = "normal" | "genis";

export interface ButonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  gorunum?: Gorunum;
  boyut?: Boyut;
  yukleniyor?: boolean;
  /**
   * Yuklenirken gosterilecek metin — fiilin SUREN hali.
   *
   * Cizim paketi bunu acikca istiyor: "Kaydet" -> "Kaydediliyor".
   * Sebebi kozmetik degil, ekran okuyucu: donen bir cark
   * `aria-hidden` oldugu icin duyurulmaz ve metin degismezse gorme
   * engelli kullanici tikladigini bile anlamaz. `aria-busy` durumu
   * soyluyor, metin ise NE oldugunu.
   *
   * Bilesen fiili kendisi cekemez (Turkce'de kural yok, istisna cok);
   * o yuzden cagiran taraf yaziyor. Verilmezse eski davranis surer.
   */
  yukleniyorMetni?: ReactNode;
  children: ReactNode;
}

/**
 * ================================================================
 * BIRINCIL DUGME MAVI — KARAR 04.09.2026
 * ================================================================
 * 05.08.2026'da turuncuya cevrilmisti (`bg-vurgu-700`) ve o karar da
 * olculerek verilmisti. 02.09.2026 tasarim teslimi birincil dugmeyi
 * MAVI cizdi; gerekcesi turuncunun kotu olmasi degil, KOMSULUGU:
 * turuncu-kirmizi bir CTA, mavi markali koyu kabugun ustunde "eylem"
 * degil "uyari" gibi okunuyor ve ayni ekranda gercek uyari renkleri
 * de var. Emre karari 04.09.2026'da tasarimdan yana verdi.
 *
 * Erisilebilirlik degismedi: gradyanin UST duragi (en acik nokta,
 * kontrasti belirleyen) `marka-600` = 5.17, onceki turuncunun 5.18'i
 * ile ayni esikte; alt durak `marka-700` = 6.70. `kontrast.test.ts`
 * ikisini de olcuyor.
 *
 * ================================================================
 * RENK DEGISMEZ, YUKSEKLIK DEGISIR
 * ================================================================
 * Hover'da rengi acmak kontrast butcesini her seferinde yeniden
 * pazarliga acar: olculen 5.17, bir sonraki ton ayarinda sessizce
 * esigin altina duser. Golgeyle kaldirmak bedava — kontrast hic
 * degismiyor.
 *
 *   dinlenme    `golge-birincil`            yuzeyin bir kademe ustunde
 *   uzerinde    `golge-birincil-uzerinde`   -1px kalkar, golge buyur
 *   basili      `golge-birincil-basili`     0'a doner, golge ICERI
 *   devre disi  HICBIR HAREKET YOK — kalkan dugme tiklanabilir
 *               soz verir; ustelik golgesini de kaybediyor, cunku
 *               dokunulabilirlik bilgisi yukseklikle tasiniyor ve
 *               yalnizca rengi soldurmak renk koru kullaniciya
 *               hicbir sey soylemez.
 *
 * ================================================================
 * HAREKET `enabled:` ILE KAPILANIYOR — OZELLIK YARISI DEGIL
 * ================================================================
 * Devre disi dugmeyi hareketsiz birakmanin iki yolu vardi: her
 * hareket sinifini `disabled:` karsitiyla EZMEK, ya da hareketi
 * bastan yalnizca etkin dugmeye vermek. Ilki ozellik agirligi
 * yarisina giriyor — `:hover` ile `:disabled` ayni agirlikta ve
 * hangisinin kazandigi Tailwind'in siralamasina kaliyor, yani
 * gorunmez bir varsayima. Ikincisi kapiyi ISIN KENDISINE bagliyor:
 * `enabled:hover:` zaten devre disi dugmede hic eslesmiyor.
 */
const GORUNUM: Record<Gorunum, string> = {
  birincil: cn(
    "bg-(image:--gradyan-birincil) text-notr-0 shadow-(--golge-birincil)",
    "enabled:hover:-translate-y-px enabled:hover:shadow-(--golge-birincil-uzerinde)",
    "enabled:active:translate-y-0 enabled:active:shadow-(--golge-birincil-basili)",
  ),
  /*
    IKINCILDE DOLGU DEGISMEZ. Uzerine gelince yalnizca halka bir kademe
    koyulasiyor ve kart golgesi geliyor — dolguyu da oynatmak, ikincil
    bir eylemi birincil kadar gurultulu yapardi. Basildiginda dolgu
    bir kademe ISINIYOR: ikincil dinlenmede zaten duz durdugu icin
    kalkacak yeri yok, o yuzden ASAGI basiliyor.
  */
  ikincil: cn(
    "bg-notr-0 text-notr-900 ring-1 ring-inset ring-notr-300",
    "enabled:hover:ring-notr-400 enabled:hover:shadow-kart",
    "enabled:active:translate-y-px enabled:active:bg-notr-50 enabled:active:ring-notr-400",
  ),
  sessiz: cn(
    "text-notr-700",
    "enabled:hover:bg-notr-100",
    "enabled:active:translate-y-px enabled:active:bg-notr-200",
  ),
  /*
    ================================================================
    RET DOLU KIRMIZI DEGIL — KARAR, CEZA DEGIL
    ================================================================
    Onceki hali `bg-hata-500 text-white` idi: dolu, doygun bir kirmizi.
    Cizim paketi bunu bilerek reddediyor ve gerekcesini yaziyor —
    bir talebi reddetmek yikici bir islem DEGIL, siradan bir karar.
    Dolu kirmizi, "sil" ile "reddet"i ayni gorsel siddete kavusturup
    ikisini de degersizlestiriyordu.

    Ayrica o dolu hal 03.08.2026'da gercekten GORUNMEZ cizilmisti
    (`tarama-kapsami.test.ts`): beyaz yazi saydam zeminde. Hatanin
    sebebi bu sinif degildi ama tekrar dolu kirmiziya donmemek,
    o dersin de yaninda duruyor.

    Kirmizi metin `hata-700`: `hata-500` kendi acik zemininde AA'nin
    altinda kaliyor (teslim notu 4,19 olcuyor).
  */
  tehlike: cn(
    "bg-notr-0 text-hata-700 ring-1 ring-inset ring-hata-200",
    "enabled:hover:bg-hata-50 enabled:hover:shadow-kart",
    "enabled:active:translate-y-px enabled:active:bg-hata-50 enabled:active:ring-2 enabled:active:ring-hata-500",
  ),
  /*
    KESİKLİ HAYALET — UNİKOÇ'un ikinci düğmesi (sürüm 9a; tarama-2 §2.9 madde 2):
    beyaz zemin, 1 px KESİK mor kenar, mor kalın yazı. "Temizle", "PDF İndir",
    "Yeni Rapor" gibi birincilin yanındaki ikincil eylemler. Mor metin
    `mor-700` (beyazda 6,52): ölçülen #9040F1 metin olarak 5,01 ile sınırda.
  */
  kesikli: cn(
    "bg-notr-0 text-mor-700 border-mor-kesikli border border-dashed font-semibold",
    "enabled:hover:bg-mor-50",
    "enabled:active:translate-y-px",
  ),
};

/**
 * ================================================================
 * DEVRE DISI GORUNUMU AYRI — CUNKU "YUKLENIYOR" DEVRE DISI DEGIL
 * ================================================================
 * Yuklenen dugme `disabled` ozelligini TASIYOR ve tasimasi da sart:
 * cift gonderim korumasi bunun uzerinde duruyor (`Buton.test.tsx`).
 * Ama GORUNTUSU devre disi olmamali.
 *
 * Ikisi ayni sey degil ve kullaniciya soyledikleri de ayni degil:
 * devre disi "bu is su an YAPILAMAZ", yukleniyor "bu is su an
 * YAPILIYOR". Ikincisini griye boyamak, kullanicinin baslattigi
 * islemi iptal olmus gibi gosteriyordu — cizim paketi de yukleniyor
 * halini gradyanini KORUYARAK, yalnizca %85 opaklikla ciziyor.
 *
 * O yuzden devre disi renkleri ayri bir tabloda ve yalnizca
 * `disabled && !yukleniyor` iken ekleniyor.
 */
const DEVRE_DISI: Record<Gorunum, string> = {
  // `bg-none` gradyani kaldirmazsa altindaki zemin rengi hic gorunmez.
  birincil: "disabled:bg-none disabled:bg-notr-200 disabled:text-notr-600 disabled:shadow-none",
  ikincil: "disabled:bg-notr-50 disabled:text-notr-500 disabled:ring-notr-200",
  sessiz: "disabled:text-notr-400",
  tehlike: "disabled:bg-notr-50 disabled:text-notr-500 disabled:ring-notr-200",
  kesikli: "disabled:text-notr-500 disabled:border-notr-300",
};

const BOYUT: Record<Boyut, string> = {
  normal: "px-4",
  genis: "w-full px-4",
};

/**
 * Temel dugme.
 *
 * `min-h-dokunma` her gorunumde SABITTIR (44px). Ogrenci arayuzu telefonda
 * tek elle kullanilacak ve bu deger 03-kod-standartlari.md'de zorunlu.
 * Kucultmek gerekiyorsa once o belge degisir.
 *
 * ================================================================
 * `min-w-dokunma` DA EKLENDI — 07.09.2026
 * ================================================================
 * Standardin cumlesi "dokunma hedefi en az 44x44 px" (iki boyut);
 * bilesen yalnizca YUKSEKLIGI tutuyordu. Genislik metinden geliyordu
 * ve metin uzun oldugu surece sorun gorunmuyordu.
 *
 * Faz kapanis turunda 375px'te olculdu: gorev kartindaki IKON
 * dugmesi ("Yapamadim" ✕) 43x44 cikti — bir piksel eksik ve tam da
 * ogrencinin gun icinde en cok bastigi ekranda.
 *
 * Cagri yerine `min-w-dokunma` yazmak da cozerdi ve unutulabilirdi:
 * kural bilesende olunca ikinci ikon dugmesini yazan kisinin
 * hatirlamasi gerekmiyor (CLAUDE.md kural 10).
 *
 * `boyut="genis"` zaten `w-full`; genis dugmelerde etkisi yok.
 */
export function Buton({
  gorunum = "birincil",
  boyut = "normal",
  yukleniyor = false,
  yukleniyorMetni,
  disabled,
  className,
  children,
  // HTML'de form icindeki `<button>` varsayilani SUBMIT. Bu, "Kapat"
  // ya da "Iptal" gibi bir dugmenin formu gondermesi demek — hem de
  // dogrulamadan gecmis gibi. Varsayilani `button` yapip gonderimi
  // ACIKCA istemek, unutulmasi mumkun olmayan tarafta durmak.
  type = "button",
  ...rest
}: ButonProps) {
  return (
    <button
      {...rest}
      type={type}
      /*
        `data-gorunum`: personel paneli (`.panel-yuzeyi`) birincil düğmenin yazı
        ağırlığını UNİKOÇ'taki gibi kalınlaştırıyor ve bunu sınıf değiştirmeden,
        kapsam seçicisiyle yapıyor — giriş ekranındaki aynı düğme değişmesin.
      */
      data-gorunum={gorunum}
      disabled={disabled || yukleniyor}
      aria-busy={yukleniyor || undefined}
      className={cn(
        "min-h-dokunma min-w-dokunma rounded-alan inline-flex items-center justify-center gap-2",
        "text-sm font-medium",
        /*
          GECIS SURESI BELIRTECTEN.
          Her bilesenin kendi zamanlamasini uydurmasi, ayni ekranda
          farkli hizlarda tepki veren dugmeler demekti — goz bunu
          "tutarsiz" diye okuyor, sebebini adlandiramadan.

          YAZIM `(--degisken)`, `[--degisken]` DEGIL. Ikincisi Tailwind
          v3 sozdizimi; v4 onu degisken BASVURUSU degil duz metin sayip
          `transition-duration: --sure-hizli` yaziyor — gecersiz deger,
          hicbir hata yok ve sure varsayilana da DUSMUYOR: gecis HIC
          olmuyor. Gecersiz birakilan bir bildirim yok sayilmiyor,
          ozelligin BASLANGIC degerine dusuruluyor ve
          `transition-duration`in baslangici 0s. Yani renk anlik
          sicriyordu. Tarayicida olculdu (04.09.2026, panelin kendi
          stil sayfasiyla): eski yazim 0s, yeni yazim 0.2s.
          Derleyiciyle olculdu 04.09.2026; ayni yazim golgede de vardi
          ve golgeyi TAMAMEN yok ediyordu.
        */
        "ease-cikis transition-[background-color,box-shadow,transform,translate,color] duration-(--sure-hizli)",
        "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed",
        // Yuklenirken dugme soluyor ama SOLMAKLA yetinmiyor: cark
        // doniyor, metin degisiyor, `aria-busy` duyuruluyor.
        yukleniyor && "opacity-85",
        GORUNUM[gorunum],
        !yukleniyor && DEVRE_DISI[gorunum],
        BOYUT[boyut],
        className,
      )}
    >
      {yukleniyor && (
        <span
          aria-hidden="true"
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {yukleniyor && yukleniyorMetni ? yukleniyorMetni : children}
    </button>
  );
}
